const VARIANT_UPDATE_EVENT = 'variant:update';
const FAVORITES_STORAGE_KEY = 'etolie_favs';
const AVAILABILITY_AVAILABLE = 'available';
const AVAILABILITY_OTHER_SIZE = 'other-size';
const AVAILABILITY_UNAVAILABLE = 'unavailable';
const CARD_COLOUR_PARAM = 'kolor';

function normalizeFavorite(raw) {
  if (typeof raw === 'string') {
    const handle = raw.trim();
    return handle ? { handle, variantId: '', color: '' } : null;
  }
  if (!raw || typeof raw !== 'object') return null;

  const handle = String(raw.handle || raw.h || '').trim();
  if (!handle) return null;
  return {
    handle,
    variantId: String(raw.variantId || raw.variant_id || raw.v || '').trim(),
    color: String(raw.color || raw.c || '').trim(),
  };
}

function favoriteHandleKey(raw) {
  const item = normalizeFavorite(raw);
  return item ? item.handle.toLocaleLowerCase('pl-PL') : '';
}

function favoriteColorKey(value) {
  return String(value || '')
    .trim()
    .toLocaleLowerCase('pl-PL')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

function favoriteKey(raw) {
  if (typeof window.etolieFavs?.key === 'function') return window.etolieFavs.key(raw);
  const item = normalizeFavorite(raw);
  if (!item) return '';
  const color = favoriteColorKey(item.color);
  return `${favoriteHandleKey(item)}::${color ? `color:${color}` : 'product'}`;
}

function compactFavorites(rawItems) {
  const normalized = (Array.isArray(rawItems) ? rawItems : [])
    .map(normalizeFavorite)
    .filter(Boolean);
  const productLevelHandles = new Set(
    normalized.filter((item) => !favoriteColorKey(item.color)).map(favoriteHandleKey)
  );
  const seen = new Set();

  return normalized.reduce((items, item) => {
    const handle = favoriteHandleKey(item);
    const isProductLevel = !favoriteColorKey(item.color);
    if (productLevelHandles.has(handle) && !isProductLevel) return items;

    const key = favoriteKey(item);
    if (!key || seen.has(key)) return items;
    seen.add(key);
    items.push(item);
    return items;
  }, []);
}

function readFavorites() {
  if (typeof window.etolieFavs?.get === 'function') return window.etolieFavs.get();

  try {
    const parsed = JSON.parse(localStorage.getItem(FAVORITES_STORAGE_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    const clean = compactFavorites(parsed);
    if (JSON.stringify(parsed) !== JSON.stringify(clean)) {
      try {
        localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(clean));
      } catch (_migrationError) {
        // Odczyt nadal działa, nawet gdy przeglądarka blokuje zapis migracji.
      }
    }
    return clean;
  } catch (_error) {
    return [];
  }
}

function writeFavorites(items) {
  if (typeof window.etolieFavs?.set === 'function') return window.etolieFavs.set(items);

  const clean = compactFavorites(items);
  try {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(clean));
  } catch (_error) {
    return false;
  }

  document.dispatchEvent(new CustomEvent('etolie:favs', { detail: { count: clean.length } }));
  return true;
}

function variantSource(element, event) {
  const productId = element.dataset.productId;
  const detail = event.detail || {};
  const data = detail.data || {};
  const html = data.html;

  if (!productId || String(data.productId || '') !== productId || !html) return null;
  return html.querySelector(`${element.localName}[data-product-id="${CSS.escape(productId)}"]`);
}

/**
 * Ś13 (28.09.2026): on the phone the colour row scrolls sideways; the chosen
 * colour starts in view. The page itself does not scroll. 28.10.2026: also on
 * products with colour only, which have no etolie-pdp-variant-availability.
 */
function revealSelectedColor(input) {
  const rail = input.closest('.variant-option__rail');
  const tile = input.closest('label');
  if (!rail || !tile || rail.scrollWidth <= rail.clientWidth) return;
  const railBox = rail.getBoundingClientRect();
  const tileBox = tile.getBoundingClientRect();
  if (tileBox.left >= railBox.left && tileBox.right <= railBox.right) return;
  rail.scrollLeft += tileBox.left - railBox.left - (railBox.width - tileBox.width) / 2;
}

/**
 * 03.11.2026 (Jarek: „wchodzę, od razu patrzę i wiem, że tu się da przesunąć”): the colour row shows an arrow
 * while it can scroll further that way, like the category row. Arrows only scroll; colours stay radios.
 * Horizon's morph drops the attributes on a colour change, so they are set again after variant:update.
 */
function updateColorRail(rail) {
  const fieldset = rail.closest('.variant-option--etolie-color');
  if (!fieldset) return;
  const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
  const canGoBack = maxScroll > 2 && rail.scrollLeft > 2;
  const canGoForward = maxScroll > 2 && rail.scrollLeft < maxScroll - 2;
  fieldset.toggleAttribute('data-can-go-back', canGoBack);
  fieldset.toggleAttribute('data-can-go-forward', canGoForward);
  const active = document.activeElement;
  const previous = rail.parentElement?.querySelector('.etolie-color-arrow--previous');
  const next = rail.parentElement?.querySelector('.etolie-color-arrow--next');
  if ((active === previous && !canGoBack) || (active === next && !canGoForward)) {
    const target = canGoBack ? previous : canGoForward ? next : rail.querySelector('input:checked');
    target?.focus({ preventScroll: true });
  }
}


function updateColorRails() {
  document.querySelectorAll('variant-picker .etolie-color-rail > .variant-option__rail').forEach(updateColorRail);
}

document.addEventListener(
  'scroll',
  (event) => {
    if (event.target instanceof Element && event.target.matches('.etolie-color-rail > .variant-option__rail')) {
      updateColorRail(event.target);
    }
  },
  { capture: true, passive: true }
);
document.addEventListener('click', (event) => {
  const arrow = event.target instanceof Element ? event.target.closest('.etolie-color-arrow') : null;
  const rail = arrow?.parentElement?.querySelector(':scope > .variant-option__rail');
  if (!rail) return;
  const direction = arrow.classList.contains('etolie-color-arrow--previous') ? -1 : 1;
  const tile = rail.querySelector('.variant-option__button-label');
  const step = (tile?.getBoundingClientRect().width || 0) + (parseFloat(getComputedStyle(rail).columnGap) || 0);
  const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
  const position = Math.min(maxScroll, Math.max(0, rail.scrollLeft + direction * (step || rail.clientWidth)));
  rail.scrollTo({
    left: position,
    behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
  });
});
document.addEventListener(VARIANT_UPDATE_EVENT, updateColorRails);
window.addEventListener('resize', updateColorRails, { passive: true });

/**
 * Owns the exact size or color/size availability matrix on the PDP. Liquid
 * emits facts; this component applies one semantic state and one visual
 * contract to every option before the native VariantPicker handles the change.
 * The chosen size is never swapped for the customer. An unavailable pair from
 * the address (?variant=) stays selected, marked unavailable, and explained by
 * the notice rendered outside the picker, so Horizon's morph cannot erase it.
 *
 * Ś4 (26.09.2026): the size is the customer's choice. Without ?variant= in the
 * address Liquid leaves it unchecked when the colour has more than one available
 * size (snippets/etolie-pdp-size-required.liquid). Until she picks one, both add
 * buttons say "Wybierz rozmiar" and product-form.js adds nothing. A colour change
 * keeps her size only when that size can be bought in the new colour.
 */
class EtoliePdpVariantAvailability extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.picker = this.closest('variant-picker');
    if (!this.picker) return;

    // A size from ?variant= (ads, feeds) counts as chosen, as before Ś4. Shop cards
    // add kolor=1: their variant picks the colour only (releaseCardSize).
    const url = new URL(window.location.href);
    this.fromCard = url.searchParams.has(CARD_COLOUR_PARAM);
    this.sizeChosen = url.searchParams.has('variant') && !this.fromCard;
    this.sizePrompted = false;

    this.abortController = new AbortController();
    this.picker.addEventListener('change', this.handleChange, {
      capture: true,
      signal: this.abortController.signal,
    });
    this.closest('.shopify-section')?.addEventListener(VARIANT_UPDATE_EVENT, this.handleVariantUpdate, {
      signal: this.abortController.signal,
    });
    this.closest('.shopify-section')?.addEventListener('etolie:size-required', this.handleSizeRequired, {
      signal: this.abortController.signal,
    });

    this.refreshMatrix();
    if (this.fromCard) this.releaseCardSize();
    this.syncAvailability();
  }

  /**
   * Ś4 from a shop card: the colour comes from the card, the size stays the
   * customer's choice when that colour has more than one size to buy.
   */
  releaseCardSize() {
    const matrix = this.matrix;
    if (!matrix) return;
    const hasColor = typeof matrix.color === 'string' && matrix.color;
    const color = hasColor ? this.selectedValue(matrix.color) : '';
    const sizes = new Set(
      matrix.variants.filter((variant) => (!hasColor || variant.c === color) && variant.a).map((variant) => variant.s)
    );
    if (sizes.size < 2) return;
    for (const input of this.optionInputs(matrix.size)) input.checked = false;
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    this.picker = null;
    this.matrix = null;
  }

  refreshMatrix() {
    const source = this.querySelector('[data-etolie-availability-matrix]');
    if (!source) {
      this.matrix = null;
      return;
    }

    try {
      const parsed = JSON.parse(source.textContent || '');
      this.matrix =
        parsed &&
        (parsed.color === null || typeof parsed.color === 'string') &&
        typeof parsed.size === 'string' &&
        Array.isArray(parsed.variants)
          ? parsed
          : null;
    } catch (_error) {
      this.matrix = null;
    }
  }

  optionInputs(name) {
    if (!this.picker || !name) return [];
    return Array.from(this.picker.querySelectorAll('input[type="radio"]')).filter(
      (input) => input instanceof HTMLInputElement && input.name.startsWith(`${name}-`)
    );
  }

  selectedValue(name) {
    const inputs = this.optionInputs(name);
    const checked = inputs.find((input) => input.checked);
    if (checked) return checked.value;

    const key = name === this.matrix?.color ? 'c' : 's';
    const values = Array.from(new Set((this.matrix?.variants || []).map((variant) => variant[key]).filter(Boolean)));
    return values.length === 1 ? values[0] : '';
  }

  setAvailability(input, state) {
    const unavailable = state === AVAILABILITY_UNAVAILABLE;
    const label = input.closest('.variant-option__button-label');

    input.dataset.etolieAvailability = state;
    input.dataset.optionAvailable = unavailable ? 'false' : 'true';
    input.disabled = unavailable;
    label?.setAttribute('data-etolie-availability', state);

    if (unavailable) {
      input.setAttribute('aria-disabled', 'true');
      input.setAttribute('aria-label', `${input.value}. ${window.etolieT('Niedostępny')}`);
      return;
    }

    input.removeAttribute('aria-disabled');
    input.setAttribute(
      'aria-label',
      state === AVAILABILITY_OTHER_SIZE
        ? `${input.value}. ${window.etolieT('Wybrany rozmiar jest w tym kolorze niedostępny.')}`
        : input.value
    );
  }

  syncAvailability() {
    const matrix = this.matrix;
    if (!matrix) return;

    const hasColor = typeof matrix.color === 'string' && matrix.color.length > 0;
    const selectedColor = hasColor ? this.selectedValue(matrix.color) : '';
    const selectedSize = this.selectedValue(matrix.size);
    // Ś4: colours are muted for a size only after the customer chose that size.
    const colorSize = this.sizeChosen ? selectedSize : '';

    if (hasColor) {
      for (const input of this.optionInputs(matrix.color)) {
        const availableInAnySize = matrix.variants.some((variant) => variant.c === input.value && variant.a);
        const availableInSelectedSize = matrix.variants.some(
          (variant) => variant.c === input.value && variant.s === colorSize && variant.a
        );
        const state = !availableInAnySize
          ? AVAILABILITY_UNAVAILABLE
          : colorSize && !availableInSelectedSize
            ? AVAILABILITY_OTHER_SIZE
            : AVAILABILITY_AVAILABLE;
        this.setAvailability(input, state);
      }
    }

    for (const input of this.optionInputs(matrix.size)) {
      const availableForSelectedColor = matrix.variants.some(
        (variant) => (!hasColor || variant.c === selectedColor) && variant.s === input.value && variant.a
      );
      this.setAvailability(
        input,
        availableForSelectedColor ? AVAILABILITY_AVAILABLE : AVAILABILITY_UNAVAILABLE
      );
    }

    const singleSize = this.picker?.querySelector('[data-etolie-single-size]');
    if (singleSize) {
      const sizeValue = singleSize.dataset.etolieSingleSizeValue || selectedSize;
      const availableForSelectedColor = matrix.variants.some(
        (variant) => (!hasColor || variant.c === selectedColor) && variant.s === sizeValue && variant.a
      );
      const state = availableForSelectedColor ? AVAILABILITY_AVAILABLE : AVAILABILITY_UNAVAILABLE;
      singleSize.dataset.etolieAvailability = state;
    }

    const pairUnavailable =
      hasColor &&
      selectedColor &&
      selectedSize &&
      !matrix.variants.some(
        (variant) => variant.c === selectedColor && variant.s === selectedSize && variant.a
      );

    const sizeInputs = this.optionInputs(matrix.size);
    const sizeRequired = sizeInputs.length > 0 && !sizeInputs.some((input) => input.checked);
    if (!sizeRequired) this.sizePrompted = false;
    this.renderSizeRequired(sizeRequired);

    // N41: a colour without any size to buy needs another colour, not another size;
    // a product sold out in every colour needs no advice at all.
    const anyAvailable = matrix.variants.some((variant) => variant.a);
    const colorSoldOut =
      hasColor && selectedColor && !matrix.variants.some((variant) => variant.c === selectedColor && variant.a);

    let notice = this.sizePrompted ? window.etolieT('Wybierz rozmiar') : '';
    if (!anyAvailable) notice = '';
    else if (colorSoldOut) notice = window.etolieT('Ten kolor jest wyprzedany. Wybierz inny kolor.');
    else if (pairUnavailable) {
      notice = window.etolieT('W kolorze {colour} rozmiar {size} jest niedostępny. Wybierz inny rozmiar.', {colour: selectedColor, size: selectedSize});
    }
    this.renderNotice(notice);
  }

  /**
   * Ś4: without a size chosen by the customer both add buttons say "Wybierz
   * rozmiar" and stay clickable (the click scrolls to the sizes). Horizon
   * re-renders the buttons for the variant Shopify resolved, so this runs after
   * every change and variant:update; once a size is chosen, the server state stays.
   */
  renderSizeRequired(required) {
    const section = this.closest('.shopify-section');
    const form = section?.querySelector('product-form-component');
    if (!section || !form) return;

    form.toggleAttribute('data-etolie-size-required', required);
    if (!required) return;

    const text = window.etolieT('Wybierz rozmiar');
    for (const button of section.querySelectorAll(
      'product-form-component [ref="addToCartButton"], sticky-add-to-cart [ref="addToCartButton"]'
    )) {
      button.disabled = false;
      const label = button.querySelector('.add-to-cart-text__content > span');
      const node = label?.firstElementChild || label;
      if (node && node.textContent !== text) node.textContent = text;
    }

    const sticky = section.querySelector('sticky-add-to-cart');
    if (sticky) sticky.dataset.variantAvailable = 'true';
    const stock = section.querySelector('etolie-pdp-stock-status');
    if (stock) stock.hidden = true;
    const assurance = section.querySelector('.etolie-pdp-assurance');
    if (assurance) assurance.hidden = false;
  }

  /**
   * The notice lives outside <variant-picker>, because Horizon morphs the whole
   * picker with server HTML. Its text is derived from the matrix on every
   * change and every variant:update, so it survives the morph instead of being
   * a one-off string written into a node this component does not own.
   */
  renderNotice(message) {
    const notice = this.closest('.shopify-section')?.querySelector('[data-etolie-variant-notice]');
    if (!notice || notice.textContent === message) return;
    notice.textContent = message;
  }

  handleChange = (event) => {
    const input = event.target;
    const matrix = this.matrix;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !matrix) return;

    // A color with no available size at all is not a purchasable choice. Every
    // other color keeps the size the customer chose only when it can be bought
    // there; otherwise the size is dropped and she chooses again (Ś4).
    if (typeof matrix.color === 'string' && matrix.color && input.name.startsWith(`${matrix.color}-`)) {
      const availableInAnySize = matrix.variants.some((variant) => variant.c === input.value && variant.a);

      if (!availableInAnySize) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      // Runs before Horizon builds the request, so a dropped size is not sent.
      const size = this.optionInputs(matrix.size).find((option) => option.checked);
      const keepSize =
        this.sizeChosen &&
        size &&
        matrix.variants.some((variant) => variant.c === input.value && variant.s === size.value && variant.a);
      if (size && !keepSize) {
        size.checked = false;
        this.sizeChosen = false;
      }
    } else if (input.name.startsWith(`${matrix.size}-`)) {
      this.sizeChosen = true;
    }

    this.syncAvailability();
  };

  handleVariantUpdate = () => {
    window.requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.refreshMatrix();
      // Ś4: Shopify checks the first size of the colour; only the customer's choice stays.
      if (!this.sizeChosen) {
        for (const input of this.optionInputs(this.matrix?.size)) input.checked = false;
      }
      this.syncAvailability();
    });
  };

  handleSizeRequired = () => {
    this.sizePrompted = true;
    this.syncAvailability();
    this.optionInputs(this.matrix?.size)[0]?.closest('fieldset')?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center',
    });
  };
}

class EtoliePdpFavorite extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.closest('.shopify-section')?.addEventListener(VARIANT_UPDATE_EVENT, this.handleVariantUpdate, {
      signal: this.abortController.signal,
    });
    this.addEventListener('click', this.handleClick, { signal: this.abortController.signal });
    document.addEventListener('etolie:favs', this.mark, { signal: this.abortController.signal });
    this.mark();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  handleVariantUpdate = (event) => {
    const source = variantSource(this, event);
    if (!source) return;

    const currentButton = this.querySelector('[data-etolie-fav]');
    const sourceButton = source.querySelector('[data-etolie-fav]');
    const sameFavorite =
      currentButton &&
      sourceButton &&
      favoriteHandleKey({ handle: currentButton.dataset.etolieFav }) ===
        favoriteHandleKey({ handle: sourceButton.dataset.etolieFav }) &&
      favoriteColorKey(currentButton.dataset.etolieFavColor) === favoriteColorKey(sourceButton.dataset.etolieFavColor);

    if (sameFavorite) {
      currentButton.dataset.etolieFavVariant = sourceButton.dataset.etolieFavVariant || '';
    } else {
      this.replaceChildren(...Array.from(source.childNodes, (node) => node.cloneNode(true)));
    }
    this.mark();
  };

  mark = () => {
    const button = this.querySelector('[data-etolie-fav]');
    if (!button) return;

    const item = normalizeFavorite({
      handle: button.dataset.etolieFav,
      variantId: button.dataset.etolieFavVariant,
      color: button.dataset.etolieFavColor,
    });
    if (!item) return;

    const favorites = readFavorites();
    const exact = favorites.some((favorite) => favoriteKey(favorite) === favoriteKey(item));
    const productLevel = favorites.some(
      (favorite) => favoriteHandleKey(favorite) === favoriteHandleKey(item) && !favoriteColorKey(favorite.color)
    );
    const active = exact || productLevel;
    const label = `${button.dataset.etolieFavLabel || item.handle}${item.color ? `, ${item.color}` : ''}`;

    button.classList.toggle('is-fav', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute('aria-label', `${active ? 'Usuń z ulubionych' : 'Zapisz w ulubionych'}: ${label}`);
    const text = button.querySelector('[data-etolie-fav-text]');
    if (text) text.textContent = active ? window.etolieT("W ulubionych") : window.etolieT("Dodaj do ulubionych");

    window.etolieMarkFavButtons?.(this);
  };

  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-etolie-fav]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();

    const item = normalizeFavorite({
      handle: button.dataset.etolieFav,
      variantId: button.dataset.etolieFavVariant,
      color: button.dataset.etolieFavColor,
    });
    if (!item) return;

    const favorites = readFavorites();
    const key = favoriteKey(item);
    let index = favorites.findIndex((favorite) => favoriteKey(favorite) === key);
    if (index < 0 && button.getAttribute('aria-pressed') === 'true') {
      index = favorites.findIndex(
        (favorite) => favoriteHandleKey(favorite) === favoriteHandleKey(item) && !favoriteColorKey(favorite.color)
      );
    }

    if (index >= 0) favorites.splice(index, 1);
    else favorites.push(item);

    // `writeFavorites` emits the single global synchronization event used by
    // the PDP, cards, header and favorites page. Do not mirror the toggle.
    writeFavorites(favorites);
  };
}

class EtoliePdpStockStatus extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.closest('.shopify-section')?.addEventListener(VARIANT_UPDATE_EVENT, this.handleVariantUpdate, {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  handleVariantUpdate = (event) => {
    const source = variantSource(this, event);
    if (!source) return;

    for (const key of ['variantId', 'inventory', 'available']) {
      if (source.dataset[key] === undefined) delete this.dataset[key];
      else this.dataset[key] = source.dataset[key];
    }
    this.hidden = source.hidden;
  };
}

class EtoliePdpContent extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.closest('.shopify-section')?.addEventListener(VARIANT_UPDATE_EVENT, this.handleVariantUpdate, {
      signal: this.abortController.signal,
    });
    this.closest('.shopify-section')?.addEventListener('click', this.handleSectionClick, {
      signal: this.abortController.signal,
    });
    this.addEventListener('click', this.handleClick, { signal: this.abortController.signal });
    this.addEventListener('toggle', this.handleToggle, {
      capture: true,
      signal: this.abortController.signal,
    });
    this.syncExternalUi();
    this.syncDescriptionToggle();
    if (location.hash === '#etolie-size-details') {
      const details = this.querySelector('#etolie-size-details');
      if (details) {
        details.open = true;
        requestAnimationFrame(() => details.scrollIntoView({ block: 'start' }));
      }
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  handleVariantUpdate = (event) => {
    const source = variantSource(this, event);
    if (!source) return;

    const openKeys = new Set(
      Array.from(this.querySelectorAll('details[open][data-etolie-accordion]'), (details) => details.dataset.etolieAccordion)
    );

    for (const key of ['variantId', 'available']) {
      if (source.dataset[key] === undefined) delete this.dataset[key];
      else this.dataset[key] = source.dataset[key];
    }

    const currentColor = favoriteColorKey(this.dataset.color);
    const sourceColor = favoriteColorKey(source.dataset.color);
    if (currentColor === sourceColor) {
      this.syncExternalUi();
      return;
    }

    this.dataset.color = source.dataset.color || '';

    this.replaceChildren(...Array.from(source.childNodes, (node) => node.cloneNode(true)));
    for (const details of this.querySelectorAll('details[data-etolie-accordion]')) {
      details.open = openKeys.has(details.dataset.etolieAccordion);
    }
    this.syncSizeGuide(source);
    this.syncExternalUi();
    this.syncDescriptionToggle();
  };

  syncSizeGuide(source) {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const sourceScope = source.closest('.shopify-section') || source.ownerDocument;
    const sourceLink = sourceScope?.querySelector('.etolie-size-guide');
    const currentLink = section.querySelector('.etolie-size-guide');

    if (!sourceLink) {
      currentLink?.remove();
      return;
    }

    const nextLink = sourceLink.cloneNode(true);
    if (currentLink) {
      currentLink.replaceWith(nextLink);
      return;
    }

    const picker = section.querySelector('variant-picker');
    if (picker) picker.after(nextLink);
  }

  handleSectionClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('.etolie-size-guide');
    const section = this.closest('.shopify-section');
    if (!link || !section?.contains(link)) return;

    const details = section.querySelector('#etolie-size-details');
    if (!(details instanceof HTMLDetailsElement)) return;
    event.preventDefault();
    details.open = true;
    details.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  handleToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (!details.matches('[data-etolie-accordion="description"]') || !details.open) return;
    this.syncDescriptionToggle();
  };

  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-etolie-description-toggle]');
    if (!button || !this.contains(button)) return;

    const copy = this.querySelector('[data-etolie-description-copy]');
    if (!copy) return;
    const expanded = !copy.classList.contains('is-expanded');
    copy.classList.toggle('is-expanded', expanded);
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    button.textContent = expanded ? window.etolieT("Pokaż mniej") : window.etolieT("Zobacz więcej");
  };

  syncDescriptionToggle() {
    window.requestAnimationFrame(() => {
      const copy = this.querySelector('[data-etolie-description-copy]');
      const button = this.querySelector('[data-etolie-description-toggle]');
      if (!copy || !button) return;
      copy.classList.remove('is-expanded');
      button.setAttribute('aria-expanded', 'false');
      button.textContent = window.etolieT("Zobacz więcej");
      button.hidden = copy.scrollHeight <= copy.clientHeight + 1;
    });
  }

  syncExternalUi() {
    const assurance = this.closest('.shopify-section')?.querySelector('.etolie-pdp-assurance');
    if (assurance) assurance.hidden = this.dataset.available !== 'true';
  }
}

class EtolieProductReviews extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.formTranslationTimers = new Set();
    this.reviewObserver = new MutationObserver(this.translateJudgeMe);
    this.reviewObserver.observe(this, { childList: true, subtree: true });
    document.addEventListener('click', this.handleDocumentClick, {
      signal: this.abortController.signal,
    });
    this.translateJudgeMe();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    this.reviewObserver?.disconnect();
    this.reviewObserver = null;
    for (const timer of this.formTranslationTimers || []) window.clearTimeout(timer);
    this.formTranslationTimers?.clear();
  }

  translateJudgeMe = () => {
    const replacements = [
      ['.jm-review-widget-minimal-header__title', window.etolieT("Opinie klientów")],
      ['.jm-no-reviews-state__text', window.etolieT("Ten produkt nie ma jeszcze opinii")],
      ['.jm-action-buttons__button', window.etolieT("Napisz opinię")],
    ];

    for (const [selector, label] of replacements) {
      for (const element of this.querySelectorAll(selector)) {
        if (element.textContent.trim() !== label) element.textContent = label;
      }
    }
  };

  handleDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const reviewAnchor = event.target.closest('a[href="#judgeme_product_reviews"]');
    if (reviewAnchor) {
      event.preventDefault();
      const offset = window.matchMedia('(max-width: 749px)').matches ? 82 : 110;
      const top = window.scrollY + this.getBoundingClientRect().top - offset;
      window.scrollTo({ top, behavior: 'smooth' });
      return;
    }

    if (!event.target.closest('.jm-action-buttons__button')) return;
    for (const delay of [100, 400]) {
      const timer = window.setTimeout(() => {
        this.formTranslationTimers.delete(timer);
        this.translateReviewForm();
      }, delay);
      this.formTranslationTimers.add(timer);
    }
  };

  translateReviewForm() {
    const dialog = document.querySelector('[role="dialog"]');
    if (!dialog) return;

    const copy = new Map([
      ['Jak oceniłbyś ten produkt?', window.etolieT("Jak oceniasz ten produkt?")],
      ['Bylibyśmy wdzięczni, gdybyś podzielił się swoimi doświadczeniami.', window.etolieT("Podziel się swoją opinią o produkcie.")],
    ]);

    for (const element of dialog.querySelectorAll('*')) {
      if (element.children.length > 0) continue;
      const replacement = copy.get(element.textContent.trim());
      if (replacement) element.textContent = replacement;
    }

    dialog
      .querySelector('button[aria-label="Close review form"]')
      ?.setAttribute('aria-label', window.etolieT("Zamknij formularz opinii"));
    const rating = dialog.querySelector('[role="group"][aria-label="Rating"]');
    rating?.setAttribute('aria-label', window.etolieT("Ocena"));
    const labels = [window.etolieT("1 gwiazdka"), window.etolieT("2 gwiazdki"), window.etolieT("3 gwiazdki"), window.etolieT("4 gwiazdki"), window.etolieT("5 gwiazdek")];
    rating?.querySelectorAll('button').forEach((button, index) => {
      if (labels[index]) button.setAttribute('aria-label', labels[index]);
    });
  }
}

if (!customElements.get('etolie-pdp-favorite')) {
  customElements.define('etolie-pdp-favorite', EtoliePdpFavorite);
}
if (!customElements.get('etolie-pdp-variant-availability')) {
  customElements.define('etolie-pdp-variant-availability', EtoliePdpVariantAvailability);
}
if (!customElements.get('etolie-pdp-stock-status')) {
  customElements.define('etolie-pdp-stock-status', EtoliePdpStockStatus);
}
if (!customElements.get('etolie-pdp-content')) {
  customElements.define('etolie-pdp-content', EtoliePdpContent);
}
if (!customElements.get('etolie-product-reviews')) {
  customElements.define('etolie-product-reviews', EtolieProductReviews);
}

document.querySelectorAll('variant-picker .variant-option--etolie-color input:checked').forEach(revealSelectedColor);
updateColorRails();
